"use client";

/**
 * Section registry — maps each storefront SectionType to a thin adapter that
 * renders the existing leaf component. Adapters merge per-section config over the
 * values ShopClient derived before; with an empty config they reproduce the
 * legacy render exactly, so an un-customized store is unchanged.
 *
 * The keys of SECTION_REGISTRY MUST equal the SectionType union in types/storefront.ts.
 */
import React from "react";
import { resolveCuratedToVisible, normalizeName } from "@/src/utils/products/groupCatalog";
import {
  HeroBanner,
  TrustStrip,
  ActiveOffers,
  BestsellersRail,
  CategoryFilterBar,
  AllProducts,
  ShopTheEdit,
  BundleAndSave,
  ReviewsBlock,
  AboutMaker,
  TopOfferBanner,
  DealOfTheDay,
} from "./components";
import type { FilterState } from "./components/SidebarFilter";
import type {
  Category,
  ComboV3,
  Coupon,
  OutletDetails,
  Product,
  Tenant,
} from "@/src/types/tenant";
import type {
  SectionType,
  HeroConfig,
  TopOfferBannerConfig,
  TrustStripConfig,
  ActiveOffersConfig,
  BestsellersConfig,
  ProductGridConfig,
  BundleAndSaveConfig,
  ReviewsConfig,
  AboutMakerConfig,
  DealOfTheDayConfig,
  ShopTheEditConfig,
} from "@/src/types/storefront";

/** Shared data bag the SSR page derives once and every adapter reads from. */
export interface ShopData {
  prefix: string;
  tenant: Tenant;
  outlet: OutletDetails | null;
  /** The branch whose catalogue is on screen. Differs from `outlet` (the
   *  primary) once the buyer has an address nearer another branch. */
  servingOutlet: OutletDetails | null;
  outletId: string;
  /** Store spans several outlets — turns on the per-card branch label. */
  /**
   * Whether cards may come from different branches. `undefined` hands the
   * decision to `OutletLocation`'s own `hasMultipleOutlets(products)`, which
   * inspects the rendered list — after grouping that is the only signal that
   * stays correct, and an explicit boolean would override it.
   */
  multiOutlet: boolean | undefined;
  /**
   * Every landing outlet id. The canonical category tree is fetched for these,
   * not for the serving branch alone: the tree's `productIds` become the
   * category filter's predicate, so a single-branch tree would filter out any
   * card held by another branch the moment a buyer taps a category tile.
   */
  allOutletIds: string[];
  categories: Category[];
  coupons: Coupon[];
  combos: ComboV3[];
  products: Product[];
  /** Every outlet's products, unfiltered. Only for translating a seller's
   *  curated ids onto the grouped catalogue — see resolveCuratedToVisible. */
  allProducts: Product[];
  /** Every outlet's combos, unfiltered. Same purpose as `allProducts`. */
  allCombos: ComboV3[];
  filteredProducts: Product[];
  filters: FilterState;
  setFilters: (f: FilterState) => void;
  displayName: string;
  subtitle: string;
  businessBrief?: string;
  dealProduct?: Product;
  /** Tenant's configured category order; see StorefrontConfig.productPriority. */
  productPriority?: string[];
}

export interface SectionRenderProps {
  data: ShopData;
  config: unknown;
}

// Helpers: resolve a hand-picked selection, preserving the seller's chosen order.
function pickById(products: Product[], id?: string | null): Product | undefined {
  return id ? products.find((p) => p._id === id) : undefined;
}
const HeroSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as HeroConfig;
  const featured = c.featuredProductId
    ? pickById(data.products, c.featuredProductId) ?? data.dealProduct
    : data.dealProduct;
  return (
    <HeroBanner
      title={c.title ?? data.displayName}
      subtitle={c.subtitle ?? (data.subtitle || "Shop local favorites on GrabLocal")}
      bannerImages={c.bannerUrls?.length ? c.bannerUrls : undefined}
      bannerImage={!c.bannerUrls?.length ? (c.bannerUrl ?? data.tenant.additionalInfo?.banner ?? undefined) : undefined}
      rating={data.tenant.outletRating?.averageRating}
      ratingCount={data.tenant.outletRating?.ratingCount}
      city={data.servingOutlet?.city ?? data.outlet?.city}
      category={data.categories[0]?.name}
      minCartValue={data.servingOutlet?.minCartValue ?? data.outlet?.minCartValue ?? null}
      featuredProduct={featured ?? null}
      outletId={data.outletId}
      prefix={data.prefix}
      ctaLabel={c.ctaLabel}
      badge={c.badge}
    />
  );
};

const TrustStripSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as TrustStripConfig;
  return <TrustStrip minCartValue={data.servingOutlet?.minCartValue ?? data.outlet?.minCartValue ?? null} city={data.servingOutlet?.city ?? data.outlet?.city} badges={c.badges} />;
};

const ActiveOffersSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as ActiveOffersConfig;
  /* Coupon ids are per-outlet Postgres row ids, so a curated list from another
   * branch matches nothing. Show this outlet's own offers rather than hiding the
   * section the seller enabled. */
  const picked = c.couponIds?.length
    ? data.coupons.filter((cp) => c.couponIds!.includes(cp.id))
    : data.coupons;
  const coupons = picked.length ? picked : data.coupons;
  if (!coupons.length) return null;
  return <ActiveOffers coupons={coupons} tenantName={data.displayName} heading={c.heading} subtext={c.subtext} />;
};

const BestsellersSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as BestsellersConfig;
  /* Seller's picks, translated to the branch this buyer is shopping. A list
   * curated against another outlet matches no `_id` here, which used to empty
   * the rail and hide the section entirely. If fewer than two picks exist at
   * this branch, fall back to the outlet's own auto-ranked list so the section
   * the seller enabled still renders. */
  const curated = resolveCuratedToVisible(c.productIds, data.allProducts, data.products);
  const products = !c.productIds?.length || curated.length < 2 ? data.products : curated;
  return (
    <BestsellersRail
      products={products}
      outletId={data.outletId}
      multiOutlet={data.multiOutlet}
      prefix={data.prefix}
      eyebrow={c.eyebrow}
      heading={c.heading}
      {...(c.limit ? { limit: c.limit } : {})}
    />
  );
};

const ProductGridSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as ProductGridConfig;
  return (
    <div className="mt-[clamp(40px,5vw,56px)]">
      <CategoryFilterBar
        categories={data.categories}
        products={data.products}
        combos={data.combos}
        /* Serving branch, matching the grid below — see AllProductsClient. */
        outletId={data.servingOutlet?.outletId ?? data.outletId}
        outletIds={data.allOutletIds}
        onFilterChange={data.setFilters}
      />
      <div className="mt-7">
        <AllProducts
          products={data.filteredProducts}
          outletId={data.outletId}
          multiOutlet={data.multiOutlet}
          priority={data.productPriority}
          limit={c.limit ?? 8}
          prefix={data.prefix}
          eyebrow={c.eyebrow}
          heading={c.heading}
        />
      </div>
    </div>
  );
};

const ShopTheEditSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as ShopTheEditConfig;
  /* Tile ids become `?ids=` on /shop/all-products, which resolves them by `_id`.
   * Translate them here or a tile curated at another branch opens an empty page. */
  const tiles = c.tiles?.map((t) => {
    if (!t.productIds?.length) return t;
    const mapped = resolveCuratedToVisible(t.productIds, data.allProducts, data.products);
    return mapped.length ? { ...t, productIds: mapped.map((p) => p._id) } : t;
  });
  return (
    <ShopTheEdit
      prefix={data.prefix}
      tiles={tiles}
      eyebrow={c.eyebrow}
      heading={c.heading}
      subtitle={c.subtitle}
      ctaLabel={c.ctaLabel}
    />
  );
};

const BundleAndSaveSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as BundleAndSaveConfig;
  /* Combos clone per outlet with their own `_id`s too — see BestsellersSection.
   * Keyed on NAME rather than the product group key: a combo carries no
   * category and no comparable price shape, and it stays narrowed to one
   * branch, so name is the identity it actually has. */
  const curated = resolveCuratedToVisible(
    c.comboIds,
    data.allCombos,
    data.combos,
    (combo) => normalizeName(combo.name)
  );
  const combos = !c.comboIds?.length || curated.length === 0 ? data.combos : curated;
  return (
    <BundleAndSave
      combos={combos}
      outletId={data.outletId}
      tenantId={data.tenant.tenantId != null ? String(data.tenant.tenantId) : undefined}
      prefix={data.prefix}
      filters={data.filters}
      heading={c.heading}
      subtitle={c.subtitle}
    />
  );
};

const ReviewsSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as ReviewsConfig;
  return (
    <ReviewsBlock
      storeName={data.displayName}
      rating={data.tenant.outletRating?.averageRating ?? null}
      ratingCount={data.tenant.outletRating?.ratingCount ?? null}
      prefix={data.prefix}
      eyebrow={c.eyebrow}
      heading={c.heading}
    />
  );
};

const AboutMakerSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as AboutMakerConfig;
  return (
    <AboutMaker
      storeName={data.displayName}
      description={c.body ?? data.businessBrief}
      banner={c.imageUrl ?? data.tenant.additionalInfo?.banner ?? undefined}
      makerUrl={(c.makerUrl ?? data.tenant.additionalInfo?.landingPage?.makerUrl) || undefined}
      websiteLink={(c.websiteLink ?? data.tenant.additionalInfo?.website_link) || undefined}
      heading={c.heading}
      mainTitle={c.mainTitle}
      founderQuote={c.founderQuote}
      ctaLabel={c.ctaLabel}
    />
  );
};

const TopOfferBannerSection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as TopOfferBannerConfig;
  return <TopOfferBanner coupons={data.coupons} offers={c.offers} speed={c.speed} />;
};

const DealOfTheDaySection: React.FC<SectionRenderProps> = ({ data, config }) => {
  const c = config as DealOfTheDayConfig;
  const product = c.productId ? pickById(data.products, c.productId) ?? data.dealProduct : data.dealProduct;
  return (
    <DealOfTheDay
      product={product ?? undefined}
      eyebrow={c.eyebrow}
      title={c.title}
      description={c.description}
      ctaLabel={c.ctaLabel}
      countdownLabel={c.countdownLabel}
      sellingPrice={c.sellingPrice}
      mrp={c.mrp}
      timerEnd={c.timerEnd}
    />
  );
};

export const SECTION_REGISTRY: Record<SectionType, React.FC<SectionRenderProps>> = {
  topOfferBanner: () => null,
  hero: HeroSection,
  trustStrip: TrustStripSection,
  activeOffers: ActiveOffersSection,
  bestsellers: BestsellersSection,
  productGrid: ProductGridSection,
  shopTheEdit: ShopTheEditSection,
  dealOfTheDay: DealOfTheDaySection,
  bundleAndSave: BundleAndSaveSection,
  reviews: ReviewsSection,
  aboutMaker: AboutMakerSection,
  // Rendered directly by ShopClient just above the footer (always on, not a
  // seller-configurable section). Null here so the config system never renders it.
  moreNeighbors: () => null,
};
